import React, { useState, useEffect } from "react";
import {
  states,
  isoDateFormatter,
  fixDate,
  displayDateFormatter,
} from "components/helpers";
import {
  addPatient,
  editPatient,
  updateEncounterDetails,
} from "apiHandler/actions";
import {
  TextField,
  InputLabel,
  Select,
  FormControl,
  Radio,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
  Modal,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import { useUserData } from "components/customHooks/userDataContext";
import dayjs from "dayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers";

const initialErrorState = {
  first_name: false,
  last_name: false,
  dob: false,
  gender: false,
  address1: false,
  address2: false,
  city: false,
  state_cd: false,
  zip: false,
  // only mandatory if consent is selected
  email: false,
  phone_no_mobile: false,
};

const AddPatientModal = ({
  dictation_id,
  onClose,
  patient_details,
  setSnackbarMessage = () => {},
}) => {
  const [patient, setPatient] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    dob: null,
    email: "",
    gender: "",
    address1: "",
    address2: "",
    city: "",
    state_cd: "",
    country: "",
    zip: "",
    height: "",
    weight: "",
    ssn: "",
    blood_group: "",
    phone_no_mobile: "",
    phone_no_home: "",
    phone_no_work: "",
    consent_email: "No",
    consent_sms: "No",
  });
  const [updateValues, setUpdateValues] = useState({});
  const [mainError, setMainError] = useState("");
  const [error, setError] = useState({ ...initialErrorState });
  const userData = useUserData();
  const isEditPatient = Boolean(patient_details);
  const token = userData?.access_token;

  useEffect(() => {
    if (patient_details) {
      setPatient({
        ...patient,
        ...patient_details,
        dob: fixDate(patient_details.dob),
      });
    }
  }, []);

  const validate = () => {
    let isValid = true;
    let tempError = { ...initialErrorState };
    if (!patient.first_name) {
      tempError["first_name"] = true;
      isValid = false;
    }
    if (!patient.last_name) {
      tempError["last_name"] = true;
      isValid = false;
    }
    if (!patient.dob) {
      tempError["dob"] = true;
      isValid = false;
    }
    if (!patient.gender) {
      tempError["gender"] = true;
      isValid = false;
    }
    if (!patient.address1) {
      tempError["address1"] = true;
      isValid = false;
    }
    if (!patient.city) {
      tempError["city"] = true;
      isValid = false;
    }
    if (!patient.state_cd) {
      tempError["state_cd"] = true;
      isValid = false;
    }
    if (!patient.zip) {
      tempError["zip"] = true;
      isValid = false;
    }
    if (patient.consent_email === "Yes") {
      if (!patient.email) {
        tempError["email"] = true;
        isValid = false;
      }
    }
    if (patient.consent_sms === "Yes") {
      if (!patient.phone_no_mobile) {
        tempError["phone_no_mobile"] = true;
        isValid = false;
      }
    }
    setError(tempError);
    return isValid;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const isValid = validate();
    if (!isValid) return;
    const payload = {
      ...patient,
      dob: displayDateFormatter(patient.dob),
      practice_id: userData?.selected_practice,
      practice_patient_id: 1,
      consent_email: patient.consent_email,
      consent_sms: patient.consent_sms,
      dictation_id,
      user_name: userData.first_name + " " + userData.last_name,
    };
    const response = await addPatient(payload, token);
    if (!response) {
      setMainError("Failed to add the patient");
      return;
    }
    if (Object.prototype.hasOwnProperty.call(response, "Address Error")) {
      setMainError(response["Address Error"]);
      return;
    }
    setSnackbarMessage("Added Patient Successfully");
    if (response) {
      const patientPayload = {
        type: "Encounter",
        record_data: {
          practice_id: userData?.selected_practice,
          patient_id: response.patient_id,
          dictation_id: dictation_id,
          scribe_id: userData.email,
          dob: displayDateFormatter(patient.dob),
          first_name: patient.first_name,
          last_name: patient.last_name,
          user_name: userData.first_name + " " + userData.last_name,
        },
      };
      const patientData = await updateEncounterDetails(patientPayload, token);
      console.log("patientData----", patientData);
      onClose(true);
    }
  };

  const onEdit = async (e) => {
    e.preventDefault();
    if (Object.keys(updateValues).length === 0) {
      onClose();
      return;
    }
    const valid = validate();
    if (!valid) return;
    const payload = {
      ...updateValues,
      patient_id: patient_details.patient_id,
      dob: isoDateFormatter(patient.dob),
    };
    const response = await editPatient(payload, token);
    if (response) {
      setSnackbarMessage("Updated Patient Successfully");
      onClose(true);
    }
  };

  const onChange = (type, value) => {
    if (isEditPatient) setUpdateValues((prev) => ({ ...prev, [type]: value }));
    setPatient((prev) => ({ ...prev, [type]: value }));
  };

  return (
    <Modal open={true} onClose={() => {}}>
      <div className="add-patient-modal">
        <header className="apm-header">
          <h1>{isEditPatient ? "Edit Patient" : "Add Patient"}</h1>
          {mainError ? <p className="apm-error">{mainError}</p> : null}
          <IconButton
            onClick={() => onClose()}
            className="apm-close"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </header>
        <Grid container spacing={2}>
          <Grid container item spacing={2}>
            <Grid item xs={12} sm={4} md={5}>
              <TextField
                variant="standard"
                id="first-name"
                label="First Name"
                className="full-width"
                error={error.first_name}
                value={patient.first_name}
                onChange={(e) => onChange("first_name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={5}>
              <TextField
                variant="standard"
                id="last-name"
                label="Last Name"
                className="full-width"
                error={error.last_name}
                value={patient.last_name}
                onChange={(e) => onChange("last_name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                variant="standard"
                id="middle-initial"
                label="M Name Intl"
                className="full-width"
                value={patient.middle_name}
                onChange={(e) => onChange("middle_name", e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container item spacing={2} style={{ marginTop: 5 }}>
            <Grid item xs={12} sm={12} md={3}>
              <TextField
                variant="standard"
                id="email"
                label="Email"
                className="full-width"
                type="email"
                error={error.email}
                style={{ marginTop: -20 }}
                value={patient.email}
                onChange={(e) => onChange("email", e.target.value)}
              />
            </Grid>
            <Grid item xs={4} sm={4} md={2}>
              <InputLabel
                id="birth-date"
                error={error.dob}
                style={{ marginTop: 8 }}
              >
                Birth Date
              </InputLabel>
            </Grid>
            <Grid item xs={8} sm={8} md={3}>
              <DesktopDatePicker
                slotProps={{ textField: { variant: "standard" } }}
                error={error.dob}
                id="birth-date"
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                value={dayjs(patient.dob)}
                onChange={(e) => onChange("dob", e)}
                sx={{ width: "80%" }}
                autoOk
              />
            </Grid>
            <Grid item xs={3} sm={4} md={1} alignContent="center">
              <InputLabel
                id="gender"
                error={error.gender}
                style={{ marginTop: 8 }}
              >
                Gender
              </InputLabel>
            </Grid>
            <Grid item xs={9} sm={8} md={3}>
              <Radio
                checked={patient.gender === "Male"}
                onChange={() => onChange("gender", "Male")}
                value="male"
                name="gender"
              />
              <span className="robotoFont">Male</span>
              <Radio
                checked={patient.gender === "Female"}
                onChange={() => onChange("gender", "Female")}
                value="female"
                name="gender"
              />
              <span className="robotoFont">Female</span>
            </Grid>
          </Grid>

          <Grid item md={12} xs={12} sm={12}>
            <TextField
              variant="standard"
              id="address"
              label="Street Address"
              className="full-width"
              error={error.address1}
              style={{ marginTop: -20 }}
              value={patient.address1}
              onChange={(e) => onChange("address1", e.target.value)}
            />
          </Grid>
          <Grid item md={12} xs={12} sm={12}>
            <TextField
              variant="standard"
              id="address2"
              label="Street Address 2"
              className="full-width"
              error={error.address2}
              style={{ marginTop: -10 }}
              value={patient.address2}
              onChange={(e) => onChange("address2", e.target.value)}
            />
          </Grid>

          <Grid container item spacing={1}>
            <Grid item xs={6} sm={6} md={4}>
              <FormControl className="full-width">
                <InputLabel id="state">State</InputLabel>
                <Select
                  variant="standard"
                  native
                  labelId="state"
                  error={error.state_cd}
                  value={patient.state_cd}
                  onChange={(e) => onChange("state_cd", e.target.value)}
                >
                  <option value=""></option>
                  {states.map((obj) => (
                    <option value={obj.value} key={obj.value}>
                      {obj.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={6} md={4}>
              <TextField
                variant="standard"
                id="city"
                label="City"
                error={error.city}
                className="full-width"
                value={patient.city}
                onChange={(e) => onChange("city", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4}>
              <TextField
                variant="standard"
                id="zipcode"
                label="Zip Code"
                type="number"
                className="full-width"
                value={patient.zip}
                error={error.zip}
                onChange={(e) => onChange("zip", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                variant="standard"
                id="ssm"
                label="SSN"
                className="full-width"
                value={patient.ssn}
                error={error.ssn}
                onChange={(e) => onChange("ssn", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                variant="standard"
                label="Mobile Phone"
                type="tel"
                value={patient.phone_no_mobile}
                className="full-width"
                error={error.phone_no_mobile}
                onChange={(e) => onChange("phone_no_mobile", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                variant="standard"
                label="Home Phone"
                type="tel"
                value={patient.phone_no_home}
                className="full-width"
                error={error.phone_no_home}
                onChange={(e) => onChange("phone_no_home", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                variant="standard"
                label="Work Phone"
                type="tel"
                value={patient.phone_no_work}
                className="full-width"
                error={error.phone_no_work}
                onChange={(e) => onChange("phone_no_work", e.target.value)}
              />
            </Grid>
          </Grid>
          <Grid item container>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={patient.consent_email === "Yes"}
                    onChange={(e) =>
                      onChange("consent_email", e.target.checked ? "Yes" : "No")
                    }
                    name="consent-email"
                  />
                }
                label="Consent to send Email"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={patient.consent_sms === "Yes"}
                    onChange={(e) =>
                      onChange("consent_sms", e.target.checked ? "Yes" : "No")
                    }
                    name="consent-sms"
                  />
                }
                label="Consent to send SMS"
              />
            </Grid>
          </Grid>
          <button
            className="main-btn"
            onClick={isEditPatient ? onEdit : onSubmit}
          >
            {isEditPatient ? "Update" : "Add Patient"}
          </button>
          <button onClick={() => onClose()} className="cancel-btn">
            Cancel
          </button>
        </Grid>
      </div>
    </Modal>
  );
};

export default AddPatientModal;
